import { useEffect, useState } from 'react';
import { Panel } from '../components/Panel';
import { addressTypeInter, userType } from '../constant/constant';
import { createDoctorValidator, updateDoctorValidator } from '../validator';
import { useAuth } from 'react-oidc-context';
import {
  createDoctor,
  getSingleDoctor,
  getState,
  setPasswordByAdminForDoc,
  updateDoctor,
} from '../service';
import {
  DoctorDataProps,
  ReactSelectOptionProp,
  ReactSelectOptionPropTwo,
  SingleDoctorItem,
} from '../interface/interface';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from '../components/Loading';
import showToast from '../components/ToastComponent';
import { EditPatientIcon } from '../assets/svgImages';
import ReactPasswordChecklist from 'react-password-checklist';
import CustomModal from '../components/CustomModal';
import { validatedPassword } from '../validator/passwordValidator';

function intialDoctorState() {
  return {
    firstName: '',
    lastName: '',
    middleName: '',
    preferredName: '',
    email: '',
    mobile: '',
    phone: '',
    hpi: '',
    prescriberNumber: '',
    providerNumber: '',
    internal: { label: 'Internal', value: true },
    aphraNumber: '',
    apExpiry: undefined,
    operatingUnderSASB: false,
  };
}

function initalAddressState() {
  return {
    unitNumber: '',
    streetNumber: '',
    streetName: '',
    suburb: '',
    city: '',
    stateId: { label: '', value: '' },
    businessName: '',
    addressType: { label: '', value: '' },
    country: 'Australia',
    postCode: '',
    isDefaultAddress: true,
  };
}

export const DoctorCU = (props: { mode: 'create' | 'update' }) => {
  const { user } = useAuth();
  const [doctorData, setDoctorData] =
    useState<DoctorDataProps>(intialDoctorState());
  const [address, setAddress] = useState(initalAddressState());
  const [stateOptions, setStateOptions] = useState<
    Array<ReactSelectOptionProp>
  >([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { doctorId } = useParams();
  const [pageLoad, setPageLoad] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [resetPasswordModal, setResetPasswordModal] = useState<boolean>(false);
  const [password, setPassword] = useState('');
  const [show, setShow] = useState<boolean>(false);

  const handleUpdates = (
    val: string | number | Date | ReactSelectOptionProp | Boolean,
    accessor: string,
    changeFor: 'user' | 'address',
  ) => {
    if (changeFor === 'user')
      setDoctorData((p: any) => ({ ...p, [accessor]: val }));
    else setAddress((p: any) => ({ ...p, [accessor]: val }));
  };

  useEffect(() => {
    if (!user?.access_token) return;

    setLoading(true);

    getState(user.access_token).then((response) => {
      if (response.status !== 200) {
        showToast({
          message: 'Unable to fetch states',
          type: 'info',
        });
        setLoading(false);
        return;
      }

      setStateOptions(
        response.data.map((d: { id: number; name: string }) => ({
          label: d.name,
          value: d.id,
        })),
      );
      setLoading(false);
    });
  }, [user?.access_token, props.mode]);

  // Update
  useEffect(() => {
    if (props.mode !== 'update' || !user?.access_token || !stateOptions.length)
      return;

    // Returning to 404
    if (isNaN(Number(doctorId))) return navigate('/404');

    setPageLoad(true);

    getSingleDoctor(user.access_token, Number(doctorId)).then((data) => {
      if (typeof data === 'number') {
        if (data === 404) navigate('/404');

        showToast({ message: 'Unable to fetch doctor', type: 'info' });
        return;
      }

      const responseData = data as SingleDoctorItem;

      setDoctorData({
        firstName: responseData?.user?.first_name,
        lastName: responseData?.user?.last_name,
        middleName: responseData?.user?.middle_name,
        preferredName: responseData?.user?.preferred_name,
        email: responseData?.user?.email,
        mobile: responseData?.user?.phone?.mobile,
        phone: responseData?.user?.phone?.home,
        hpi: responseData?.hpi,
        prescriberNumber: responseData?.prescriber_number,
        providerNumber: responseData?.provider_number,
        internal: userType.find(
          (x) => x.value === !!responseData?.internal,
        ) as ReactSelectOptionPropTwo,
        aphraNumber: responseData?.ahpra_number,
        apExpiry: responseData?.ap_expiry
          ? new Date(responseData?.ap_expiry)
          : undefined,
        operatingUnderSASB: responseData?.operatingUnderSASB,
      });

      if (responseData.address) {
        setAddress({
          unitNumber: responseData?.address?.unit_number,
          streetNumber: responseData?.address?.street_number,
          streetName: responseData?.address?.street_name,
          suburb: responseData?.address?.suburb,
          city: responseData?.address?.city,
          stateId: stateOptions.find(
            (sI) => Number(sI.value) === responseData?.address?.state?.id,
          ) as ReactSelectOptionProp,
          businessName: responseData?.address?.business_name,
          addressType: addressTypeInter.find(
            (aT) => responseData?.address?.address_type === aT.value,
          ) as ReactSelectOptionProp,
          country: 'Australia',
          postCode: responseData?.address?.post_code?.toString(),
          isDefaultAddress: responseData?.address?.default,
        });
      }
      setPageLoad(false);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.access_token, props.mode, doctorId, loading]);

  useEffect(() => {
    if (!resetPasswordModal) {
      setPassword('');
    }
  }, [resetPasswordModal]);

  const createDoctorHandle = async () => {
    // Validating Section
    if (!user?.access_token) return;
    const payload: Record<string, any> = {
      ...doctorData,
      internal: doctorData.internal.value,
      apExpiry: doctorData?.apExpiry ? new Date(doctorData.apExpiry) : null,
    };

    if (JSON.stringify(address) !== JSON.stringify(initalAddressState())) {
      payload.address = {
        ...address,
        stateId: (address.stateId as ReactSelectOptionProp)?.value,
        addressType: (address.addressType as ReactSelectOptionProp)?.value,
      };
    }

    const validate = createDoctorValidator(payload);

    if (validate) {
      console.log(validate);
      setError(true);
      return;
    }

    const status = await createDoctor(user.access_token, payload);

    if (status === 201) {
      showToast({ message: 'Doctor onboarded', type: 'success' });
      // Resetting the forms
      setDoctorData(intialDoctorState());
      setAddress(initalAddressState());
      navigate('/doctors');
      return;
    }

    showToast({
      message: 'Unable to onboard doctor',
      type: 'info',
    });
  };

  if (loading || pageLoad) return <Loading />;

  const updateUserHandle = async () => {
    if (!user?.access_token) return;
    const payload = {
      ...doctorData,
      internal: doctorData.internal.value,
      apExpiry: doctorData?.apExpiry ? new Date(doctorData.apExpiry) : null,
      doctorId,
      address: {
        ...address,
        stateId: (address.stateId as ReactSelectOptionProp)?.value,
        addressType: (address.addressType as ReactSelectOptionProp)?.value,
      },
    };

    const validate = updateDoctorValidator(payload);

    if (validate) {
      showToast({
        message: 'Validation Error!',
        type: 'info',
      });
      return;
    }

    const status = await updateDoctor(user.access_token, payload);

    if (status === 200) {
      showToast({ message: 'Doctor Updated!', type: 'success' });
      // Resetting the forms
      if (props.mode === 'create') {
        setDoctorData(intialDoctorState());
        setAddress(initalAddressState());
      }
      navigate('/doctors');
      return;
    }

    showToast({
      message: 'Unable to update doctor',
      type: 'info',
    });
  };

  const resetPassword = async () => {
    if (!user?.access_token) {
      showToast({
        type: 'warning',
        message: 'Access Token not found',
      });
      return;
    }

    setPasswordByAdminForDoc(
      password,
      Number(doctorId as string),
      user.access_token,
    )
      .then((res) => {
        showToast({
          type: res.status === 200 ? 'success' : 'warning',
          message: res.data.message,
        });
      })
      .catch((err) => {
        console.log(err);
        showToast({
          message: 'Failed to Update Password',
          type: 'error',
        });
      });
    setResetPasswordModal(false);
  };

  const handleSetPassword = () => {
    if (!doctorData.email) {
      showToast({
        message: 'Doctor email mandatory for set password',
        type: 'warning',
      });
      return;
    }

    setResetPasswordModal(true);
  };

  return (
    <>
      <Panel
        name="Personal Information"
        inputs={[
          {
            label: 'First Name',
            value: doctorData.firstName,
            accessor: 'firstName',
            type: 'text',
            isRequired: true,
            error:
              error && !doctorData.firstName ? 'First Name is Required' : '',
            onUpdate: (ele, accessor) => handleUpdates(ele, accessor, 'user'),
          },
          {
            label: 'Last Name',
            value: doctorData.lastName,
            accessor: 'lastName',
            isRequired: true,
            type: 'text',
            error: error && !doctorData.lastName ? 'Last Name is Required' : '',
            onUpdate: (ele, accessor) => handleUpdates(ele, accessor, 'user'),
          },
          {
            label: 'Middle Name',
            value: doctorData.middleName,
            accessor: 'middleName',
            type: 'text',
            onUpdate: (ele, accessor) => handleUpdates(ele, accessor, 'user'),
          },
          {
            label: 'Preferred Name',
            value: doctorData.preferredName,
            accessor: 'preferredName',
            type: 'text',
            onUpdate: (ele, accessor) => handleUpdates(ele, accessor, 'user'),
          },
          // {
          //   label: 'Gender',
          //   value: doctorData.gender,
          //   accessor: 'gender',
          //   type: 'select',
          //   isRequired: true,
          //   error:
          //     error && !doctorData.gender.value ? 'Gender is Required' : '',
          //   options: genders,
          //   onUpdate: (ele, accessor) => handleUpdates(ele, accessor, 'user'),
          // },
          {
            label: 'Email',
            value: doctorData.email,
            accessor: 'email',
            type: 'text',
            onUpdate: (ele, accessor) => handleUpdates(ele, accessor, 'user'),
          },
          {
            label: 'Contact (mobile)',
            value: doctorData.mobile,
            accessor: 'mobile',
            type: 'pre-text',
            preTextContent: '+61',
            onUpdate: (ele, accessor) => handleUpdates(ele, accessor, 'user'),
          },
          {
            label: 'Contact (home)',
            value: doctorData.phone,
            accessor: 'phone',
            type: 'pre-text',
            preTextContent: '+61',
            onUpdate: (ele, accessor) => handleUpdates(ele, accessor, 'user'),
          },
          // {
          //   label: 'Date of Birth',
          //   value: doctorData.dob,
          //   accessor: 'dob',
          //   type: 'date',
          //   isRequired: true,
          //   error: error && !doctorData.dob ? 'Date of Birth is Required' : '',
          //   onUpdate: (ele, accessor) => handleUpdates(ele, accessor, 'user'),
          // },
          {
            label: 'HPI',
            value: doctorData.hpi,
            accessor: 'hpi',
            type: 'text',
            onUpdate: (ele, accessor) => handleUpdates(ele, accessor, 'user'),
          },
          {
            label: 'Prescriber Number',
            value: doctorData.prescriberNumber,
            accessor: 'prescriberNumber',
            type: 'text',
            onUpdate: (ele, accessor) => handleUpdates(ele, accessor, 'user'),
          },
          {
            label: 'Provider Number',
            value: doctorData.providerNumber,
            accessor: 'providerNumber',
            type: 'text',
            onUpdate: (ele, accessor) => handleUpdates(ele, accessor, 'user'),
          },
          {
            label: 'Aphra Number',
            value: doctorData.aphraNumber,
            accessor: 'aphraNumber',
            type: 'text',
            onUpdate: (ele, accessor) => handleUpdates(ele, accessor, 'user'),
          },
          {
            label: 'AP Expiry',
            value: doctorData.apExpiry,
            accessor: 'apExpiry',
            type: 'date',
            onUpdate: (ele, accessor) => handleUpdates(ele, accessor, 'user'),
            isDisabled: doctorData?.operatingUnderSASB,
          },
          {
            label: 'Doctor Type',
            value: doctorData.internal,
            accessor: 'internal',
            type: 'select',
            options: userType,
            onUpdate: (ele, accessor) => handleUpdates(ele, accessor, 'user'),
          },
          {
            label: '',
            value: doctorData.operatingUnderSASB,
            accessor: 'operatingUnderSASB',
            type: 'checkBox',
            onUpdate: (ele, accessor) => handleUpdates(ele, accessor, 'user'),
            isDisabled: Boolean(doctorData?.apExpiry),
          },
        ]}
      />
      <div className="mt-2">
        <Panel
          name="Address Information"
          inputs={[
            {
              label: 'Unit Number',
              value: address.unitNumber,
              accessor: 'unitNumber',
              type: 'number',
              onUpdate: (ele, accessor) =>
                handleUpdates(ele, accessor, 'address'),
            },
            {
              label: 'Street Number',
              value: address.streetNumber,
              accessor: 'streetNumber',
              type: 'number',
              onUpdate: (ele, accessor) =>
                handleUpdates(ele, accessor, 'address'),
            },
            {
              label: 'Street Name',
              value: address.streetName,
              accessor: 'streetName',
              type: 'text',

              onUpdate: (ele, accessor) =>
                handleUpdates(ele, accessor, 'address'),
            },
            {
              label: 'Suburb',
              value: address.suburb,
              accessor: 'suburb',
              type: 'text',

              onUpdate: (ele, accessor) =>
                handleUpdates(ele, accessor, 'address'),
            },
            {
              label: 'City',
              value: address.city,
              accessor: 'city',
              type: 'text',

              onUpdate: (ele, accessor) =>
                handleUpdates(ele, accessor, 'address'),
            },
            {
              label: 'State',
              value: address.stateId,
              accessor: 'stateId',
              type: 'select',
              options: stateOptions,

              onUpdate: (ele, accessor) =>
                handleUpdates(ele, accessor, 'address'),
            },
            {
              label: 'Business Name',
              value: address.businessName,
              accessor: 'businessName',
              type: 'text',
              onUpdate: (ele, accessor) =>
                handleUpdates(ele, accessor, 'address'),
            },
            {
              label: 'Address Type',
              value: address.addressType,
              accessor: 'addressType',
              type: 'select',
              options: addressTypeInter,
              onUpdate: (ele, accessor) =>
                handleUpdates(ele, accessor, 'address'),
            },
            {
              label: 'Country',
              value: address.country,
              accessor: '',
              type: 'text',
              onUpdate: (ele, accessor) =>
                handleUpdates(ele, accessor, 'address'),
            },
            {
              label: 'ZIP / Postal Code',
              value: address.postCode,
              accessor: 'postCode',
              type: 'number',
              onUpdate: (ele, accessor) =>
                handleUpdates(ele, accessor, 'address'),
            },
          ]}
        />
        <div className="flex items-center justify-end gap-x-6 py-8">
          {(user?.profile.groups as string[]).includes('Admin') ? (
            <button
              className="bg-white flex font-semibold text-sm text-primary-blue-400  border-2 
                    rounded-md px-6 py-3 border-primary-blue-400 my-4 mr-2"
              onClick={() => handleSetPassword()}
            >
              <EditPatientIcon />
              <span className="ml-4">Set Password</span>
            </button>
          ) : (
            <></>
          )}
          <button
            type="button"
            className="bg-white border border-solid border-primary-blue-500 text-primary-blue-500 w-24 px-4 py-2 rounded"
            onClick={() => {
              if (props.mode === 'create') {
                setDoctorData(intialDoctorState());
                setAddress(initalAddressState());
              } else {
                navigate('/doctors');
              }
            }}
          >
            {props.mode === 'create' ? 'Clear' : 'Cancel'}
          </button>
          <button
            type="submit"
            onClick={() =>
              props.mode === 'create'
                ? createDoctorHandle()
                : updateUserHandle()
            }
            className="bg-primary-blue-500 border border-solid border-primary-blue-500 text-white px-4 py-2 w-24 rounded"
          >
            {props.mode === 'create' ? 'Save' : 'Update'}
          </button>
        </div>
      </div>

      <CustomModal
        isOpen={resetPasswordModal}
        onClose={() => setResetPasswordModal(false)}
        header="Reset Password"
        footer={
          <div>
            <button
              type="button"
              className={` ${!validatedPassword(password) && 'opacity-50'}
                    mt-10 rounded-md bg-secondary-cornflowerblue-600 px-10 py-2 text-center text-sm font-semibold text-white`}
              onClick={() => resetPassword()}
              disabled={!validatedPassword(password)}
            >
              Set Password
            </button>
            <button
              onClick={() => setResetPasswordModal(false)}
              className="text-sm px-3 py-2 bg-white border border-primary-blue-500 text-primary-blue-500 rounded-md ml-3"
            >
              Cancel
            </button>
          </div>
        }
        modalSize="w-10/12 md:w-5/12 3xl:w-3/12"
      >
        <div className="mt-7 text-lg leading-8 text-gray-600 ">
          <div className="relative mt-2 rounded-md shadow-sm">
            <input
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              name="account-number"
              id="account-number"
              className="text-sm font-regular border  
                    rounded-md p-2 w-full p-3
                    focus:border-primary-blue-500"
              placeholder="Reset Password"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              {show ? (
                <span onClick={() => setShow(!show)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 
                          0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </span>
              ) : (
                <span onClick={() => setShow(!show)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 
                          12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 
                          4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 
                          6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                    />
                  </svg>
                </span>
              )}
            </div>
          </div>

          <div className="mt-4 password-checklist">
            <ReactPasswordChecklist
              rules={['minLength', 'specialChar', 'number', 'capital']}
              minLength={8}
              value={password}
              specialCharsRegex={
                /^(?=.*[!@#$%^&*()\-=_+[\]{};':"\\|,.<>?])(?!.*(1234|aaaa|abcd)).*$/g
              }
              messages={{
                specialChar:
                  'Password should contain special character and does not contain any sequences',
              }}
            />
          </div>

          <div className="text-center"></div>
        </div>
      </CustomModal>
    </>
  );
};
